import React, { useEffect, useRef, useState } from 'react';
import { RoleAndPermissionModel } from '../../../models/roleAndPermission';
import axios from '../../../lib/axios';
import { OFFICER_PROFILE_UPDATE } from '../../../lib/endpoints/officerProfile';
import { EMPLOYEE_CREATE, EMPLOYEE_UPDATE } from '../../../lib/endpoints/employee';
import moment from 'moment';
import { GenderEnum } from '../../../enums/genderEnum';
import { IMAGE_ADMIN_PROFILE } from '../../../lib/endpoints/profileImage';
import { generateImageFileNameWithRandomName } from '../../../utils/generateImageFileName';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import { useSelector } from 'react-redux';
import provinceData from '../../../data/laos_provinces.json';
import { RootState } from '../../../store';
import { AdminEnumType } from '../../../enums/adminEnum';
import { District } from '../../../models/district';
import { ProvinceModel } from '../../../models/province';
const dataProvinceFromJson: ProvinceModel[] = provinceData;

interface Props {
  selectedAdmin: any;
  onClose: Function;
  onFetchData: Function;
  setSelected: any;
  adminType: AdminEnumType;
  roleAndPermissionData: RoleAndPermissionModel[];
}

const useAdminAccountFormController = ({
  selectedAdmin,
  onClose,
  onFetchData,
  setSelected,
  adminType,
  roleAndPermissionData,
}: Props) => {
  // ------------ State & Root State --------------//

  const [banUser, setBanUser] = useState<boolean>(false);
  const [roleAndPermissionValue, setRoleAndPermissionValue] = useState<string>(
    roleAndPermissionData?.filter((item: RoleAndPermissionModel) => {
      if (adminType === AdminEnumType.SUPER_ADMIN) {
        return item?.role_type === 'SUPER_ADMIN';
      } else if (adminType === AdminEnumType.ADMIN) {
        return item?.role_type === 'ADMIN';
      }
      return false;
    })[0]?.id
  );

  const [merchantValue, setMerchantValue] = React.useState<any>([]);
  const [provinceValue, setProvinceValue] = useState<string>(null!);
  const [districtsValue, setDistrictsValue] = useState<string>(null!);
  const [districtsData, setDistrictsData] = useState<District[]>([]);

  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [selectedGender, setSelectedGender] = useState<GenderEnum>(GenderEnum.OTHER);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null!);
  const [loadingConfirmForm, setLoadingConfirmForm] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const fullName = useRef<HTMLInputElement>(null!);
  const phone = useRef<HTMLInputElement>(null!);
  const email = useRef<HTMLInputElement>(null!);
  const dateOfBirth = useRef<HTMLInputElement>(null!);
  const village = useRef<HTMLInputElement>(null!);
  const userName = useRef<HTMLInputElement>(null!);
  const password = useRef<HTMLInputElement>(null!);
  const confirmPassword = useRef<HTMLInputElement>(null!);

  const roleAdmin = useSelector((state: RootState) => state?.auth?.data?.role?.role_type);

  const { t } = useTranslation('global');

  // ------------ State & Root State End --------------//

  // ------------- Call Api --------------------------//

  const handleAddAdmin = async () => {
    try {
      setLoadingConfirmForm(true);
      const formRegister = {
        username: userName.current.value,
        password: password.current.value,
        roleId: roleAndPermissionValue,
        merchants: merchantValue,
      };
      const res = await axios.post(EMPLOYEE_CREATE, formRegister);
      const profileId = res.data?.data?.id;
      const formData = new FormData();
      formData.append('fullName', fullName.current.value);
      formData.append('phone', phone.current.value);
      formData.append('email', email.current.value);
      formData.append('province', provinceValue);
      formData.append('city', districtsValue);
      formData.append(
        'dateOfBirth',
        moment(new Date(dateOfBirth.current.value)).format('DD/MM/YYYY')
      );
      formData.append('village', village.current.value);
      formData.append('gender', selectedGender);
      formData.append('file', generateImageFileNameWithRandomName(imageFile));

      await axios.patch(OFFICER_PROFILE_UPDATE + profileId, formData);
      await onFetchData();
      handleReset();
      onClose();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmForm(false);
      setOpenConfirm(false);
    }
  };

  const handleEditAdmin = async () => {
    try {
      setLoadingConfirmForm(true);
      const EmployeeForm = {
        username: userName.current.value,
        roleId: roleAndPermissionValue,
        isBan: banUser,
        merchants: merchantValue,
      };
      await axios.patch(EMPLOYEE_UPDATE + selectedAdmin.id, EmployeeForm);
      const officerProfileId = selectedAdmin?.profile?.id;
      const formData = new FormData();
      formData.append('fullName', fullName.current?.value);
      formData.append('phone', phone.current?.value);
      formData.append('email', email.current?.value);
      formData.append('province', provinceValue);
      formData.append('city', districtsValue);
      formData.append(
        'dateOfBirth',
        moment(new Date(dateOfBirth.current.value)).format('DD/MM/YYYY')
      );
      formData.append('village', village.current?.value);
      if (selectedGender) {
        formData.append('gender', selectedGender);
      }
      if (imageFile) {
        formData.append('file', generateImageFileNameWithRandomName(imageFile));
      }
      await axios.patch(OFFICER_PROFILE_UPDATE + officerProfileId, formData);
      await onFetchData();
      handleReset();
      onClose();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmForm(false);
      setOpenConfirm(false);
    }
  };

  // ------------- Call Api --------------------------//

  //------ function -------//

  const handleSelectedImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  const handleValidateForm = () => {
    if (!selectedAdmin && !imageFile) {
      setErrorMessage('Please select an image.');
      return false;
    }
    if (!/^\d+$/.test(phone.current?.value)) {
      setErrorMessage('Phone number must contain only digits.');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.current?.value)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }
    if (!selectedGender) {
      setErrorMessage('Please select a gender.');
      return false;
    }
    if (!provinceValue) {
      setErrorMessage('Please select a province.');
      return false;
    }
    if (!districtsValue) {
      setErrorMessage('Please select a districts.');
      return false;
    }
    if (adminType === AdminEnumType.ADMIN) {
      if (merchantValue.length < 1) {
        setErrorMessage('Please select Branch.');
        return false;
      }
    }
    if (!dateOfBirth.current?.value) {
      setErrorMessage('Please enter your date of birth.');
      return false;
    }
    if (phone.current?.value.length < 8) {
      setErrorMessage('Phone number must be at least greater than or equal to 8 digits');
      return false;
    }
    if (password.current?.value !== confirmPassword.current?.value) {
      setErrorMessage('Password and Confirm Password do not match.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleOpenConfirmFormAdmin = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirm(!openConfirm);
    }
  };

  const handleDateChange = () => {
    const inputDate = selectedAdmin?.profile?.dateOfBirth;
    const dateObject = moment(inputDate, 'DD/MM/YYYY');
    const formattedDate = dateObject.format('YYYY-MM-DD');

    return formattedDate;
  };

  const handleReset = () => {
    setSelected(null);
    setImageToShow(null);
    setImageFile(null);

    if (fullName.current) fullName.current.value = '';
    if (phone.current) phone.current.value = '';
    if (email.current) email.current.value = '';
    if (dateOfBirth.current) dateOfBirth.current.value = '';
    if (village.current) village.current.value = '';
    if (userName.current) userName.current.value = '';
    if (password.current) password.current.value = '';
    if (confirmPassword.current) confirmPassword.current.value = '';
  };

  const handleSubmit = () => {
    if (selectedAdmin?.id) {
      handleEditAdmin();
    } else {
      handleAddAdmin();
    }
  };

  const roleData = roleAndPermissionData?.filter((item: RoleAndPermissionModel) => {
    if (item?.id === roleAndPermissionValue) {
      return true;
    }
    return false;
  });

  //-------- useEffect -------//

  useEffect(() => {
    if (selectedAdmin) {
      setImageToShow(IMAGE_ADMIN_PROFILE + selectedAdmin?.profile?.image?.url);
      fullName.current.value = selectedAdmin.profile?.fullName;
      userName.current.value = selectedAdmin.username;
      phone.current.value = selectedAdmin.profile?.phone;
      email.current.value = selectedAdmin.profile?.email;
      setProvinceValue(selectedAdmin.profile?.province);
      setDistrictsValue(selectedAdmin.profile?.city);
      village.current.value = selectedAdmin.profile?.village;
      setSelectedGender(selectedAdmin.profile?.gender as GenderEnum);
      setMerchantValue(selectedAdmin?.merchants);
      setRoleAndPermissionValue(selectedAdmin?.roleId);
      dateOfBirth.current.value = handleDateChange();
      setBanUser(selectedAdmin?.isBan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdmin]);

  useEffect(() => {
    if (provinceValue && dataProvinceFromJson?.length > 0) {
      const districtData = dataProvinceFromJson.filter(
        (item) => item?.provinceNameLA === provinceValue
      )[0]?.districts;

      if (districtData?.length > 0) {
        setDistrictsData(districtData);
        setDistrictsValue(districtData[0]?.districtNameLA || null!);
      } else {
        setDistrictsData(districtData);
        setDistrictsValue(null!);
      }
      if (selectedAdmin) {
        setDistrictsValue(selectedAdmin?.profile?.city);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceValue]);

  return {
    roleAndPermissionValue,
    merchantValue,
    handleSelectedImage,
    imageToShow,
    selectedGender,
    email,
    village,
    fullName,
    phone,
    dateOfBirth,
    userName,
    password,
    confirmPassword,
    handleOpenConfirmFormAdmin,
    handleSubmit,
    errorMessage,
    handleAddAdmin,
    openConfirm,
    loadingConfirmForm,
    showPassword,
    showConfirmPassword,
    handleReset,
    banUser,
    roleAdmin,
    provinceValue,
    provinceData,
    districtsValue,
    districtsData,
    roleData,
    handleSelectMerchant: (event: SelectChangeEvent) => setMerchantValue(event.target.value),
    handleCloseConfirmForm: () => setOpenConfirm(false),
    handleShowConfirmPassword: () => setShowConfirmPassword((prevState) => !prevState),
    handleShowPassword: () => setShowPassword((prevState) => !prevState),
    handleSelectedDistrict: (e: any) => setDistrictsValue(e.target.value),
    handleSelectedGender: (e: any) => setSelectedGender(e.target.value),
    handleSelectedProvince: (e: any) => setProvinceValue(e.target.value),
    handleChangeRoleAndPermissionValue: (newValue: string) => setRoleAndPermissionValue(newValue),
    handleBanUser: (newValue: boolean) => setBanUser(newValue),
  };
};
export default useAdminAccountFormController;
